'use client';

import { useEffect, useState } from 'react';

import {
  useBehaviorSubject,
  useBehaviorSubjectUpdater,
} from '@ps/functions/hooks/useBehaviorSubject';
import { BehaviorSubject } from 'rxjs';

import { fetchChannels } from '../functions/channel/fetchChannels';
import { subscribeToChannels } from '../functions/channel/subscribeToChannels';
import type { CommunityChannel } from '../types/CommunityChannel';

interface ChannelsState {
  channels: CommunityChannel[];
  isLoading: boolean;
  error: Error | undefined;
}

const kInitialState: ChannelsState = {
  channels: [],
  isLoading: true,
  error: undefined,
};

/**
 * Channels list state for the open creator. Seeds from the search
 * endpoint on mount, then folds Firestore `onSnapshot` changes onto
 * the in-memory list. Re-orders by `updatedAt` desc after every
 * patch so newly-active channels float to the top.
 *
 * Returns a single `state$` BehaviorSubject so downstream callers
 * can subscribe selectively (`useBehaviorSubject(state$)`) instead
 * of all re-rendering on every patch.
 */
export function useCommunityChannels(creatorId: string | null | undefined) {
  const [state$] = useState(
    () => new BehaviorSubject<ChannelsState>(kInitialState),
  );

  useEffect(() => {
    if (!creatorId) {
      state$.next(kInitialState);
      return;
    }

    let cancelled = false;
    state$.next({ ...state$.getValue(), isLoading: true, error: undefined });

    void fetchChannels(creatorId)
      .then(channels => {
        if (cancelled) return;
        state$.next({
          channels: sortChannels(channels),
          isLoading: false,
          error: undefined,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        state$.next({
          channels: state$.getValue().channels,
          isLoading: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    const unsubscribe = subscribeToChannels(creatorId, change => {
      const current = state$.getValue().channels;
      if (change.type === 'removed') {
        state$.next({
          ...state$.getValue(),
          channels: current.filter(c => c.id !== change.channelId),
        });
        return;
      }
      // Merge added/modified — preserve `lastMessage` from the
      // existing row if the Firestore doc doesn't carry one (the
      // search-derived `lastMessage` is richer than what the doc
      // itself stores).
      const existing = current.find(c => c.id === change.channel.id);
      const merged: CommunityChannel = existing
        ? {
            ...existing,
            ...change.channel,
            lastMessage: change.channel.lastMessage ?? existing.lastMessage,
          }
        : change.channel;
      const filtered = current.filter(c => c.id !== merged.id);
      state$.next({
        ...state$.getValue(),
        channels: sortChannels([...filtered, merged]),
      });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [creatorId, state$]);

  return state$;
}

/**
 * Hook flavor that reads the current snapshot (rather than the
 * subject) — useful for components that need to re-render on every
 * change.
 */
export function useCommunityChannelsSnapshot(
  creatorId: string | null | undefined,
): ChannelsState {
  const state$ = useCommunityChannels(creatorId);
  const [state] = useBehaviorSubject(state$);
  return state;
}

/**
 * Caller-driven manual refresh — re-fetches via the search endpoint
 * and replaces the channels array. Used by error-retry buttons and
 * by the create-channel modal on success (so the new channel shows
 * up immediately even if the Firestore sub hasn't fired yet).
 */
export function useRefreshChannels(
  state$: BehaviorSubject<ChannelsState>,
  creatorId: string | null | undefined,
) {
  const update = useBehaviorSubjectUpdater(state$);
  return async () => {
    if (!creatorId) return;
    try {
      const channels = await fetchChannels(creatorId);
      update(current => ({
        ...current,
        channels: sortChannels(channels),
        isLoading: false,
        error: undefined,
      }));
    } catch (error: unknown) {
      update(current => ({
        ...current,
        isLoading: false,
        error: error instanceof Error ? error : new Error(String(error)),
      }));
    }
  };
}

function sortChannels(channels: CommunityChannel[]): CommunityChannel[] {
  return [...channels].sort((a, b) => {
    const aTime = a.updatedAt ?? '';
    const bTime = b.updatedAt ?? '';
    if (aTime === bTime) return 0;
    return aTime > bTime ? -1 : 1;
  });
}
